/**
 * UUID v4 chạy được cả trên HTTP thường (#19819).
 *
 * `crypto.randomUUID()` chỉ tồn tại trong **secure context** — HTTPS hoặc `localhost`. Trên một
 * deploy HTTP truy cập qua IP (dev `.4`, demo nội bộ, LAN phòng khám không có TLS) nó KHÔNG tồn
 * tại, nên lời gọi ném `TypeError: crypto.randomUUID is not a function` và nút bấm chết trước khi
 * gửi request. Anh Tỷ tái hiện trên `http://192.168.100.4:18100`: form Đặt lịch báo lỗi đỏ và
 * **không có `POST /api/v1/appointments` nào được gửi**.
 *
 * Đo trong một non-secure context thật (Chromium, `http://<lan-ip>:18799`) trước khi viết hàm này:
 *
 * ```
 * isSecureContext: false   hasCrypto: true
 * hasRandomUUID: false  →  TypeError: c.randomUUID is not a function
 * hasGetRandomValues: true   hasSubtle: false
 * ```
 *
 * Nên `getRandomValues` là chỗ dựa ĐÚNG: nó không bị cổng secure-context chặn (chỉ `randomUUID` và
 * `crypto.subtle` bị), và nó vẫn là nguồn ngẫu nhiên mật mã. KHÔNG có tầng thứ ba dùng
 * `Math.random`: mọi trình duyệt có `crypto` đều có `getRandomValues`, nên tầng đó là mã chết — và
 * một tầng chết lặng lẽ hạ chất lượng ngẫu nhiên thì tệ hơn là không có.
 *
 * <b>Vì sao đúng dạng UUID v4 chứ không phải một chuỗi ngẫu nhiên nào cũng được.</b> Hôm nay không
 * chỗ gọi nào GHI giá trị này xuống server: bốn chỗ dùng nó làm header `Idempotency-Key` (server
 * đọc chuỗi tự do), ba chỗ dùng làm khoá React cho phần tử danh sách, và payload lưu đơn thuốc
 * `Omit` hẳn trường `id`. Giữ đúng dạng v4 là để hàm có MỘT hợp đồng cho cả hai loại chỗ dùng, nên
 * chỗ gọi tiếp theo không phải tự hỏi mình đang nhận gì — không phải vì có cột nào đang đòi.
 *
 * Bản vá trong console của anh Tỷ đã chứng minh đường còn lại thông: gắn polyfill rồi bấm lại →
 * `POST /api/v1/appointments` trả 201, ba lịch được tạo thật (4CF707E1, AFBFB341, D51F0FA6).
 */
export function randomUuid(): string {
  const c: Crypto | undefined = globalThis.crypto;

  if (typeof c?.randomUUID === "function") {
    return c.randomUUID();
  }

  const b = new Uint8Array(16);
  c.getRandomValues(b);
  // Hai ô bắt buộc của RFC 4122: phiên bản 4 ở nửa cao byte 6, biến thể 10xx ở nửa cao byte 8.
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;

  const hex: string[] = [];
  for (let i = 0; i < 16; i++) {
    hex.push(b[i].toString(16).padStart(2, "0"));
  }
  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}
